import { Buffer } from 'buffer';
import _ from 'lodash';
import { captureException } from 'src/shared/helpers/captureException';

import { config } from 'src/config/config';
import { httpClient } from 'src/config/http';
import { NavigationService } from 'src/shared/services/NavigationService';
import { ToastNotificationsService } from 'src/shared/services/ToastNotificationsService';
import { analyticsClient, EventType } from 'src/shared/services/analytics';

import {
    AuthData,
    Credentials,
    JWTData,
    PasswordGrant,
    RefreshGrant,
    TokenType,
} from '../models/auth';

const AUTH_KEY: string = 'auth';

let cachedAuthData: AuthData | null;
try {
    cachedAuthData = getAuthDataFromLocalStorage();
} catch (error) {
    captureException(error);
}

function saveAuthData(authData: AuthData) {
    localStorage.setItem(AUTH_KEY, JSON.stringify(authData));
    reloadCachedAuthData();
}

interface AccessAuthData {
    accessToken: string;
    tokenType: string;
}

const tokenPath = 'oauth/token';

export class AuthenticationService {
    constructor(
        private toastNotificationsService: ToastNotificationsService,
        private navigationService: NavigationService,
    ) {}

    public get hasCachedAuthData() {
        return !!getCachedAuthData();
    }

    public redirectToApp(useBranchIo) {
        const authData = getCachedAuthData();

        if (!authData) {
            return;
        }

        let appLoginUrl;
        const newToken = authData.refresh_token;

        if (useBranchIo) {
            appLoginUrl = `https://modernhealth.app.link/token-login?token=${newToken}`;
        } else {
            appLoginUrl = `modernhealth://token-login/${newToken}`;
        }

        window.location.replace(appLoginUrl);
    }

    public async logout(): Promise<void> {
        // Keep these for now to remove from user's localStorage
        // See https://joinmodernhealth.atlassian.net/browse/CIRCLE-1173
        localStorage.removeItem('requestedUrl');
        localStorage.removeItem('requestedUrlParams');

        localStorage.removeItem(AUTH_KEY);
        let oldAuthData = getCachedAuthData();

        cachedAuthData = null;

        // Let the back-end know to revoke the refresh token
        try {
            await httpClient.DELETE_UNAUTHENTICATED<AuthData>(tokenPath, oldAuthData);
        } catch {
            // Do nothing, but catch the rejected promise
        }
    }

    // also used to validate if provided password is correct in the `change password` process
    public async login(credentials: Credentials): Promise<void> {
        const data: PasswordGrant = {
            grant_type: 'password',
            scope: 'webapp',
            username: credentials.email,
            password: credentials.password,
        };

        try {
            const authData = await httpClient.POST_UNAUTHENTICATED<AuthData, PasswordGrant>(
                tokenPath,
                data,
                { urlEncoded: true },
            );

            saveAuthData(authData);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            const errorMessage = error?.body?.error || '';
            const accountExpired = errorMessage.includes('Your account has expired');

            analyticsClient.track('Login: Wrong Credentials', EventType.API, {
                page_name: 'LoginForm',
                status_code: error?.code,
                error_message: errorMessage,
            });

            if (accountExpired) {
                this.navigationService.navigate('/ineligible');

                return Promise.reject('');
            }

            switch (error.code) {
                // Unauthenticated, i.e. an Authorization header is not provided.
                case 401: {
                    return Promise.reject(config.messages.incorrectCredentials);
                }
                // Forbidden, i.e. the user doesn't have access to login.
                case 403: {
                    return Promise.reject(config.messages.incorrectCredentials);
                }
                // Too many requests, i.e. they've incorrectly logged in too many times.
                case 429:
                    return Promise.reject(error.body.error);
                default:
                    return Promise.reject(config.messages.connectionProblemError);
            }
        }
    }

    // Perform an arbitrary HTTP request, adding auth headers transparently,
    // as well as catching unauthorized responses and handling them appropriately
    public async requestWithAuth(url: string, options: RequestInit): Promise<Response> {
        const authData = await this.getValidAuthDataOrRefresh();

        options.headers = options.headers as Headers;
        if (authData !== null) {
            options.headers.append(
                'Authorization',
                `${authData.tokenType} ${authData.accessToken}`,
            );
        }

        return fetch(url, options);
    }

    public async refreshAuthData() {
        // Local storage could have been updated by another tab
        let authData = getCachedAuthData();

        if (!authData?.refresh_token) {
            throw new Error('Refresh token is invalid');
        }

        if (!isTokenValid('refresh_token', authData)) {
            // Refetch auth data from local storage in case it has been updated by another tab.
            authData = reloadCachedAuthData();

            if (!authData || !isTokenValid('refresh_token', authData)) {
                throw new Error('Refresh token is invalid');
            }
        }

        return await this.refreshByToken();
    }

    // Refresh the access token if needed
    public async getValidAuthDataOrRefresh(): Promise<AccessAuthData | null> {
        let authData = getCachedAuthData();

        if (!authData) return null;

        let accessTokenValid = isTokenValid('access_token', authData);

        if (!accessTokenValid) {
            // Refetch auth data from local storage in case it has been updated by another tab.
            authData = reloadCachedAuthData();

            if (!authData) return null;

            if (authData) {
                accessTokenValid = isTokenValid('access_token', authData);
            }
        }

        if (accessTokenValid) {
            return {
                accessToken: authData.access_token,
                tokenType: authData.token_type,
            };
        }

        const refreshedAuthData = await this.refreshAuthData();

        return {
            accessToken: refreshedAuthData.accessToken,
            tokenType: refreshedAuthData.tokenType,
        };
    }

    public async refreshByToken(
        // If a refresh token isn't provided, always use the current value
        // in localStorage
        refreshToken: string | undefined = reloadCachedAuthData()?.refresh_token,
    ): Promise<AccessAuthData> {
        if (!refreshToken) {
            this.logout();
            throw new Error('No session found');
        }

        const data: RefreshGrant = {
            grant_type: 'refresh_token',
            scope: 'webapp',
            refresh_token: refreshToken,
        };

        try {
            const newAuthData = await httpClient.POST_UNAUTHENTICATED<AuthData, RefreshGrant>(
                tokenPath,
                data,
                { urlEncoded: true },
            );

            saveAuthData({
                refresh_token: refreshToken,
                ...newAuthData,
            });

            return { accessToken: newAuthData.access_token, tokenType: newAuthData.token_type };
        } catch (error) {
            const accountExpired = (_.get(error, 'body.error') || '').includes(
                'Your account has expired',
            );

            this.logout();

            if (accountExpired) {
                this.navigationService.navigate('/ineligible');
                throw new Error('Account expired');
            }

            this.toastNotificationsService.renderWarningToast('Your session has expired.');
            throw new Error('Expired session');
        }
    }
}

/**
 * Gets auth data from localStorage, using the locally cached version
 * if present.
 */
function getCachedAuthData(): AuthData | null {
    if (!cachedAuthData) {
        // Local storage could have been updated by another tab
        return reloadCachedAuthData();
    }

    return cachedAuthData;
}

/**
 * Reloads auth data from localStorage
 */
function reloadCachedAuthData(): AuthData | null {
    // Local storage could have been updated by another tab
    cachedAuthData = getAuthDataFromLocalStorage();

    return cachedAuthData;
}

/**
 * Fetch saved auth data from local storage
 */
function getAuthDataFromLocalStorage(): AuthData | null {
    const item = localStorage.getItem(AUTH_KEY);

    if (!item) return null;

    try {
        return JSON.parse(item);
    } catch (error) {
        captureException(error);
        return null;
    }
}

/**
 * Parse the JWT from the auth data for a given token type
 */
function parseJWT(authData: AuthData, tokenType: TokenType): JWTData | undefined {
    try {
        const token = authData[tokenType];

        if (!token) {
            return undefined;
        }

        const decoded = Buffer.from(token.split('.')[1], 'base64').toString('ascii');

        return JSON.parse(decoded);
    } catch (error) {
        captureException(error);

        return undefined;
    }
}

/**
 * Validate if the passed token has expired
 */
function isTokenValid(tokenType: TokenType, authData: AuthData): boolean {
    let payload: JWTData | undefined;
    try {
        payload = parseJWT(authData, tokenType);
    } catch (error) {
        captureException(error);

        return false;
    }

    if (!payload?.exp) {
        return false;
    }

    const expirationDateTime = payload.exp * 1000;
    const now = Date.now();

    return expirationDateTime > now;
}
